import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePlanRestrictions, handlePlanRestrictionError } from '../services/planService';
import { useToast } from '@chakra-ui/react';
import { useUserContext } from './UserContext';

const PlanRestrictionContext = createContext();

export const usePlanRestriction = () => {
  const context = useContext(PlanRestrictionContext);
  if (!context) {
    throw new Error('usePlanRestriction must be used within a PlanRestrictionProvider');
  }
  return context;
};

export const PlanRestrictionProvider = ({ children }) => {
  const [upgradeModal, setUpgradeModal] = useState({
    isOpen: false,
    restrictionType: null,
    restrictionMessage: null,
  });
  
  const toast = useToast();
  
  // Get plan restrictions from the service
  const restrictions = usePlanRestrictions();
  
  // Get user context to check if user is branch user
  const { isAdmin, userRole } = useUserContext();

  // Handle upgrade modal
  const showUpgradeModal = (type, message) => {
    setUpgradeModal({
      isOpen: true,
      restrictionType: type,
      restrictionMessage: message,
    });
  };

  const hideUpgradeModal = () => {
    setUpgradeModal({
      isOpen: false,
      restrictionType: null,
      restrictionMessage: null,
    });
  };

  // Handle upgrade action
  const handleUpgrade = (planName) => {
    // Navigate to settings page with subscription tab active
    // For admin users, subscription is at tab index 3
    // For branch users, we'll redirect to settings and show a message
    const { userRole } = JSON.parse(localStorage.getItem('userRole') || '{}');
    const isAdmin = userRole === 'admin';
    
    if (isAdmin) {
      // Navigate to settings with subscription tab active
      window.location.href = '/settings?tab=subscription';
    } else {
      // For branch users, navigate to settings and show message
      window.location.href = '/settings';
    }
    
    hideUpgradeModal();
    
    toast({
      title: isAdmin ? 'Redirecting to Subscription' : 'Access Restricted',
      description: isAdmin 
        ? 'Please review and upgrade your plan in the Subscription section.'
        : 'Subscription management is restricted to administrators.',
      status: isAdmin ? 'info' : 'warning',
      duration: 3000,
      isClosable: true,
    });
  };

  // Handle API errors that might be plan restrictions
  const handleApiError = (error) => {
    const restrictionError = handlePlanRestrictionError(error);
    
    if (restrictionError) {
      showUpgradeModal(
        restrictionError.type,
        restrictionError.message
      );
      return true; // Error was handled
    }
    
    return false; // Error was not a plan restriction
  };

  // Check if user can access a specific feature
  const canAccessFeature = (feature) => {
    // Branch users cannot access AI Analytics, Performance Reports, Productivity Reports, and Payroll
    // This is role-based restriction, not plan-based
    if (userRole === 'branch_user') {
      switch (feature) {
        case 'ai_features':
        case 'productivity_reports':
        case 'performance_reports':
        case 'advanced_analytics':
        case 'payroll':
        case 'enhanced_payroll':
          console.log(`🚫 Branch user access denied for: ${feature}`);
          return false; // Branch users cannot access these features
        default:
          return true; // Other features available to branch users
      }
    }
    
    // All Main Branch users now have access to ALL features regardless of plan
    // Only employee limits are enforced, not feature restrictions
    return true;
  };

  // Check if user can add employees
  const canAddEmployees = () => {
    return restrictions.canAddEmployees;
  };

  // Get current plan name
  const getCurrentPlanName = () => {
    return restrictions.planName;
  };

  // Get employee limit info
  const getEmployeeLimits = () => {
    return restrictions.employeeLimits;
  };

  // Check if employee limit is reached
  const isEmployeeLimitReached = () => {
    return restrictions.isEmployeeLimitReached;
  };

  // Get upgrade requirements
  const getUpgradeRequirements = () => {
    return restrictions.upgradeRequirements;
  };

  // Show feature restriction message (only for branch users now)
  const showFeatureRestriction = (featureName) => {
    // Only branch users get restriction messages
    if (userRole === 'branch_user') {
      const message = `The ${featureName} feature is only available to administrators. Branch users cannot access this feature.`;
      showUpgradeModal('branch_restriction', message);
    }
    // Main branch users no longer get plan-based restriction messages
  };

  // Show employee limit restriction message
  const showEmployeeLimitRestriction = () => {
    const message = `You have reached the maximum number of employees allowed in your current plan. Upgrade to add more employees.`;
    showUpgradeModal('employee_limit', message);
  };

  // Show branch user restriction message
  const showBranchUserRestriction = (featureName) => {
    const message = `The ${featureName} feature is only available to administrators. Branch users cannot access this feature.`;
    showUpgradeModal('branch_restriction', message);
  };

  const value = {
    // Plan restrictions
    restrictions,
    
    // Feature access checks
    canAccessFeature,
    canAddEmployees,
    isEmployeeLimitReached,
    
    // Plan information
    getCurrentPlanName,
    getEmployeeLimits,
    getUpgradeRequirements,
    
    // Modal management
    upgradeModal,
    showUpgradeModal,
    hideUpgradeModal,
    handleUpgrade,
    
    // Error handling
    handleApiError,
    
    // Convenience methods
    showFeatureRestriction,
    showEmployeeLimitRestriction,
    showBranchUserRestriction,
  };

  return (
    <PlanRestrictionContext.Provider value={value}>
      {children}
    </PlanRestrictionContext.Provider>
  );
}; 